import React, { useEffect, useState } from 'react';
import {
  Drawer,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
} from 'antd';
import { BankOutlined, SaveOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { TIPO_USO_OPTIONS } from 'providers/Edificios';

const { Title } = Typography;

// Drawer para crear/editar un edificio. El CRUD vive en useEdificios — este
// componente sólo compone el form y delega create/update al parent.
// Props:
// - open: boolean
// - edificio: fila existente (si viene, modo edit; si no, modo create)
// - onClose(): cerrar sin guardar
// - onSubmit(values): async. Recibe payload ya serializado con fechas en string.
function EdificioEditorDrawer({ open, edificio, onClose, onSubmit }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = Boolean(edificio?.id);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (edificio) {
      form.setFieldsValue({
        nombre: edificio.nombre ?? '',
        direccion: edificio.direccion ?? '',
        comuna: edificio.comuna ?? '',
        ciudad: edificio.ciudad ?? '',
        rol_predio: edificio.rol_predio ?? '',
        tipo_uso: edificio.tipo_uso ?? 'residencial',
        num_unidades: edificio.num_unidades ?? null,
        fecha_recepcion_dom: edificio.fecha_recepcion_dom
          ? dayjs(edificio.fecha_recepcion_dom)
          : null,
        ano_construccion: edificio.ano_construccion ?? null,
      });
    } else {
      form.setFieldsValue({ tipo_uso: 'residencial' });
    }
  }, [open, edificio, form]);

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        nombre: values.nombre?.trim(),
        direccion: values.direccion?.trim(),
        comuna: values.comuna?.trim() || null,
        ciudad: values.ciudad?.trim() || null,
        rol_predio: values.rol_predio?.trim() || null,
        tipo_uso: values.tipo_uso,
        num_unidades: values.num_unidades ?? null,
        fecha_recepcion_dom: values.fecha_recepcion_dom
          ? dayjs(values.fecha_recepcion_dom).format('YYYY-MM-DD')
          : null,
        ano_construccion: values.ano_construccion ?? null,
      };
      await onSubmit(payload);
      message.success(isEdit ? 'Edificio actualizado' : 'Edificio creado');
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'No se pudo guardar el edificio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      title={
        <Space align="center">
          <BankOutlined />
          <Title level={5} style={{ margin: 0 }}>
            {isEdit ? `Editar edificio #${edificio.id}` : 'Nuevo edificio'}
          </Title>
        </Space>
      }
      placement="right"
      width={640}
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="primary"
            icon={isEdit ? <SaveOutlined /> : <PlusOutlined />}
            loading={submitting}
            onClick={handleFinish}
          >
            {isEdit ? 'Guardar' : 'Crear edificio'}
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Form.Item
          label="Nombre del edificio"
          name="nombre"
          rules={[
            { required: true, message: 'El nombre es obligatorio' },
            { max: 180, message: 'Máximo 180 caracteres' },
          ]}
        >
          <Input placeholder="Ej: Edificio Torre Mirador" />
        </Form.Item>

        <Form.Item
          label="Dirección"
          name="direccion"
          rules={[
            { required: true, message: 'La dirección es obligatoria' },
            { max: 240, message: 'Máximo 240 caracteres' },
          ]}
        >
          <Input placeholder="Av. Providencia 1234" />
        </Form.Item>

        <Space.Compact block>
          <Form.Item label="Comuna" name="comuna" style={{ flex: 1, marginRight: 8 }}>
            <Input placeholder="Providencia" />
          </Form.Item>
          <Form.Item label="Ciudad" name="ciudad" style={{ flex: 1 }}>
            <Input placeholder="Santiago" />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          label="Rol predio"
          name="rol_predio"
          tooltip="Identificador municipal del predio (SII)"
        >
          <Input placeholder="1234-56" />
        </Form.Item>

        <Form.Item
          label="Tipo de uso"
          name="tipo_uso"
          rules={[{ required: true, message: 'Selecciona el tipo de uso' }]}
        >
          <Select options={TIPO_USO_OPTIONS} />
        </Form.Item>

        <Space.Compact block>
          <Form.Item
            label="N° unidades"
            name="num_unidades"
            style={{ flex: 1, marginRight: 8 }}
            tooltip="Departamentos / oficinas / locales del edificio"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="120" />
          </Form.Item>
          <Form.Item label="Año construcción" name="ano_construccion" style={{ flex: 1 }}>
            <InputNumber min={1800} max={2100} style={{ width: '100%' }} placeholder="1998" />
          </Form.Item>
        </Space.Compact>

        <Form.Item
          label="Fecha recepción DOM"
          name="fecha_recepcion_dom"
          tooltip="Fecha oficial de recepción municipal"
        >
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export default EdificioEditorDrawer;
